/**
 * The storage port the document lifecycle depends on.
 *
 * The reducer in `machine.ts` decides *what* must happen and returns it as
 * data; this interface is *how* it happens. Keeping the two apart is what lets
 * the same rules run over a real filesystem in Electron, the Origin Private
 * File System in a browser, and app-private storage on Android and iOS —
 * without the rules knowing which.
 *
 * Every method is expected to reject rather than throw synchronously, and the
 * controller turns a rejection into the `save-failed` command that keeps the
 * working copy alive (§13).
 */
import type { OriginalRef, RecoveryPoint, SavedRef, WorkingCopyRef } from './types.js';

export interface FileStat {
  readonly sizeBytes: number;
  readonly modifiedAtMs: number;
  readonly exists: boolean;
}

export type SaveOutcome =
  | { readonly ok: true; readonly ref: SavedRef }
  | { readonly ok: false; readonly reason: SaveFailure };

export type SaveFailure =
  | 'storage-full' | 'permission-denied' | 'permission-expired'
  | 'name-conflict' | 'io-error' | 'cancelled';

/** Raised by a store when it can classify the failure. */
export class FileStoreError extends Error {
  readonly reason: SaveFailure;

  constructor(reason: SaveFailure, message: string) {
    super(message);
    this.name = 'FileStoreError';
    this.reason = reason;
  }
}

export interface FileStore {
  /* ---- reading ---- */

  stat(handle: string): Promise<FileStat>;
  /**
   * Read a byte range. The primary read path: §33 forbids pulling whole files
   * into memory when a slice will do, and detection only ever needs the head.
   */
  readRange(handle: string, start: number, end: number): Promise<Uint8Array>;
  /** Whole-file read, for parsers that genuinely need it. */
  readAll(handle: string): Promise<Uint8Array>;

  /* ---- working copy (§13 step 3) ---- */

  createWorkingCopy(original: OriginalRef, sessionId: string): Promise<WorkingCopyRef>;
  writeWorkingCopy(ref: WorkingCopyRef, bytes: Uint8Array): Promise<void>;
  readWorkingCopy(ref: WorkingCopyRef): Promise<Uint8Array>;
  /** Delete it. Only ever called when there is nothing left to lose (§13). */
  releaseWorkingCopy(ref: WorkingCopyRef): Promise<void>;

  /* ---- recovery (§23) ---- */

  writeRecoveryPoint(
    sessionId: string, revision: number, bytes: Uint8Array, atMs: number,
  ): Promise<RecoveryPoint>;
  listRecoveryPoints(sessionId?: string): Promise<RecoveryPoint[]>;
  readRecoveryPoint(point: RecoveryPoint): Promise<Uint8Array>;
  deleteRecoveryPoints(points: readonly RecoveryPoint[]): Promise<void>;

  /* ---- saving (§13 steps 7–8) ---- */

  /** Does a file with this name already exist at the save destination? */
  exists(name: string): Promise<boolean>;
  saveAs(bytes: Uint8Array, suggestedName: string, revision: number): Promise<SaveOutcome>;
  overwriteOriginal(
    original: OriginalRef, bytes: Uint8Array, revision: number,
  ): Promise<SaveOutcome>;

  /* ---- housekeeping (§29) ---- */

  /** Remove temporary artefacts older than the retention window. */
  sweepCache(olderThanMs: number, nowMs: number): Promise<number>;
}

/**
 * Supplies the bytes to write.
 *
 * The controller never holds the document model — a handler does. This callback
 * is how the current bytes are obtained at exactly the moment they are needed,
 * so an auto-save always captures the latest edit rather than a stale snapshot.
 */
export type BytesProvider = (revision: number) => Promise<Uint8Array>;

/** Confirmation prompts the controller must not answer for the user (§4.10). */
export interface ConfirmationPort {
  /** §13 step 8 — replacing the original. */
  confirmOverwriteOriginal(original: OriginalRef): Promise<boolean>;
  /** §13 — a different file already has the chosen name. */
  confirmReplaceExisting(name: string): Promise<boolean>;
  /** §13/§16/§17 — features this format cannot keep. */
  acknowledgeLoss(items: readonly string[]): Promise<boolean>;
}

/** Everything the controller reports outward, as i18n keys (§25). */
export interface NotificationPort {
  notify(messageKey: string, detail?: string): void;
}
